<script lang="ts">
  import { page } from '$app/stores';

  import { createEventDispatcher } from 'svelte';

  import { Icon } from '@shared/ui/icon';
  import { is } from '@shared/utils';

  import { generateNavigationOptions } from '../libs/helpers';
  import type { NavigationOption } from '../types';

  const dispatch = createEventDispatcher();

  export let totalItems = 0;
  export let pageSize = 1;
  export let currentPage = 1;
  export let limit: number | undefined = undefined;
  export let showStepOptions = false;

  $: options = generateNavigationOptions({
    totalItems,
    pageSize,
    currentPage,
    limit,
    showStepOptions,
  });

  $: totalPages = Math.ceil(totalItems / pageSize);

  function handleOptionClick(option: NavigationOption) {
    dispatch('setPage', { page: option.value });
  }

  $: href = (value: number) => {
    let basePath = $page.url.pathname;
    const pageSegmentIndex = basePath.indexOf('/page-');

    if (pageSegmentIndex !== -1) {
      basePath = basePath.substring(0, pageSegmentIndex);
    }

    if (basePath !== '/' && basePath.endsWith('/')) {
      basePath = basePath.slice(0, -1);
    }

    const newPageSegment = value > 1 ? `/page-${value}` : '';

    const searchParams = new URLSearchParams($page.url.searchParams);
    searchParams.delete('loadMore');
    const queryString = searchParams.toString() ? `/?${searchParams.toString()}` : '';

    return basePath + newPageSegment + queryString;
  };
</script>

{#if totalItems > pageSize}
  <nav aria-label="Pagination">
    <ul class="pagination-nav flex w-full">
      {#each options as option}
        <li
          class="block transition-colors
					{is(
            option.value === currentPage,
            'text-default-900 dark:text-white',
            'text-default-500 hover:text-default-900 decoration-black/50 dark:decoration-white/50 dark:hover:text-white',
          )}  "
          class:w-8="{option.type === 'number'}"
          class:mr-auto="{option.type === 'symbol' && option.symbol === 'PREVIOUS_PAGE'}"
          class:ml-auto="{option.type === 'symbol' && option.symbol === 'NEXT_PAGE'}"
          class:disabled="{(option.type === 'symbol' && option.symbol === 'NEXT_PAGE' && currentPage >= totalPages) ||
            (option.type === 'symbol' && option.symbol === 'PREVIOUS_PAGE' && currentPage <= 1)}"
          class:w-10="{option.type === 'symbol' && option.symbol === 'ELLIPSIS'}"
          class:active="{option.type === 'number' && option.value === currentPage}"
          aria-current="{option.value === currentPage ? 'page' : null}"
        >
          <a
            class="flex h-full w-full items-center justify-center"
            href="{href(option.value)}/"
            on:click|preventDefault="{() => handleOptionClick(option)}"
            data-sveltekit-preload-data="off"
          >
            {#if option.type === 'number'}
              <span>{option.value}</span>
            {:else if option.type === 'symbol' && option.symbol === 'ELLIPSIS'}
              <span>...</span>
            {:else if option.type === 'symbol' && option.symbol === 'PREVIOUS_PAGE'}
              <div
                class="group h-full items-center capitalize {is(
                  currentPage <= 1,
                  'pointer-events-none invisible hidden sm:flex',
                  'flex',
                )}"
              >
                <div
                  class="duration-primary mr-2 inline-block rotate-90 transition-transform group-hover:-translate-x-1/4"
                >
                  <Icon name="carrot" />
                </div>
                prev
              </div>
            {:else if option.type === 'symbol' && option.symbol === 'NEXT_PAGE'}
              <div
                class="group flex h-full items-center capitalize {is(
                  currentPage >= totalPages,
                  'pointer-events-none invisible hidden sm:flex',
                  'flex',
                )}"
              >
                next
                <div
                  class="duration-primary ml-2 inline-block -rotate-90 transition-transform group-hover:translate-x-1/4"
                >
                  <Icon name="carrot" />
                </div>
              </div>
            {/if}
          </a>
        </li>
      {/each}
    </ul>
  </nav>
{/if}
